import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BookOpen, CheckCircle, XCircle, RefreshCw } from 'lucide-react'
import { api } from '../../lib/api'
import { KaijuLoader } from '../KaijuLoader'
import { NotebookLmAuthModal } from './NotebookLmAuthModal'

export function NotebookLmAuthSection() {
  const [showModal, setShowModal] = useState(false)

  const { data: status, isLoading, refetch } = useQuery({
    queryKey: ['settings', 'notebooklm-status'],
    queryFn: () => api.settings.notebooklm.status(),
    retry: false,
    refetchOnWindowFocus: false,
  })

  return (
    <>
      <div className="glass-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              NotebookLM Integration
            </h3>
          </div>
          <button
            onClick={() => refetch()}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Refresh status"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <KaijuLoader size="sm" />
            <span>Checking connection...</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {status?.authenticated ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-700 dark:text-green-400">
                    Connected
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    (cookies: {status.cookieAge} old)
                  </span>
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-700 dark:text-red-400">
                    {status?.error || 'Authentication expired'}
                  </span>
                  {status?.lastExtracted && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      (last authenticated {status.cookieAge} ago)
                    </span>
                  )}
                </>
              )}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              NotebookLM uses Google session cookies for authentication.
              These expire periodically and need to be refreshed.
            </p>

            <button
              onClick={() => setShowModal(true)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                status?.authenticated
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {status?.authenticated ? 'Re-authenticate' : 'Authenticate Now'}
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <NotebookLmAuthModal
          onClose={() => {
            setShowModal(false)
            refetch()
          }}
        />
      )}
    </>
  )
}
